import { ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IIncidentCode, IIncidentCodesByGroup, IncidentCodeService } from '@shared/services/incident-code-service';
import { EmployeeService, IMesUser } from '@shared/services/mesuser.service';
import { IItemOperation, ProductionDataService } from '@shared/services/production-data.service';
import { IProductionNotePostRequest } from '@shared/services/production-note.service';
import { TerminalAdministrationService } from '@shared/services/terminalAdministration.service';
import { forkJoin, take } from 'rxjs';

@Component({
	selector: 'app-terminal-create-reorder-dialog',
	templateUrl: './terminal-create-reorder-dialog.component.html',
	styleUrl: './terminal-create-reorder-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class TerminalCreateReorderDialogComponent implements OnInit {
	@Output() reordered: EventEmitter<any> = new EventEmitter();
	form: FormGroup;
	sequenceNumber: number;
	httpError: string = '';
	reordableSequenceNumbers: number[];
	incidentCodesByGroup: IIncidentCodesByGroup[] = [];
	employees: IMesUser[];
	loading: boolean = false;

	constructor(
		@Inject(MAT_DIALOG_DATA) public chosenItemOperation: IItemOperation,
		private dialogRef: MatDialogRef<TerminalCreateReorderDialogComponent>,
		private fb: FormBuilder,
		private productionDataService: ProductionDataService,
		private incidentCodeService: IncidentCodeService,
		private employeeService: EmployeeService,
		private terminalService: TerminalAdministrationService,
	) {
		this.reordableSequenceNumbers = this.chosenItemOperation.items.filter((item) => !item.reordered && !item.completed && item.ready).map((item) => item.sequenceNumber);

		if (this.reordableSequenceNumbers.length > 0) {
			this.sequenceNumber = this.reordableSequenceNumbers[0];
		}

		this.loadSharedData();
	}

	ngOnInit(): void {
		this.initializeForm();
	}

	initializeForm(): void {
		this.form = this.fb.group({
			sequenceNumber: [this.sequenceNumber, [Validators.required]],
			incidentCode: [null, [Validators.required]],
			note: [''],
			employee: [null, [Validators.required, (control: { value: string }) => (!control.value || !this.employees || this.employees.some((employee) => employee.alias === control.value) ? null : { employeeNotFound: true })]],
		});
		if (!this.reordableSequenceNumbers?.length) {
			this.form.disable();
		}
	}

	private loadSharedData(): void {
		forkJoin({
			employees: this.employeeService.getEmployees().pipe(take(1)),
			incidentCodes: this.incidentCodeService.getAll().pipe(take(1)),
			terminal: this.terminalService.getTerminalById(this.chosenItemOperation.terminalId).pipe(take(1)),
		}).subscribe(({ employees, incidentCodes, terminal }) => {
			this.employees = employees.result.filter((e) => e.isActive);
			this.form?.controls['employee']?.updateValueAndValidity();
			this.incidentCodesByGroup = this.incidentCodeService.filterIncidentCodesByGroups(incidentCodes, terminal.incidentCodeGroups);
		});
	}

	lastSelectedValue: string = '';
	onSelectionChange() {
		const noteControl = this.form.controls['note'];
		const incidentControl = this.form.controls['incidentCode'];
		const noteControlValue: string = noteControl.value;

		if (noteControlValue === '' || noteControlValue.startsWith(this.lastSelectedValue)) {
			const inci: IIncidentCode = incidentControl.value;
			const newValue = `(${inci.alias}) ${inci.name}\n`;
			noteControl.setValue(newValue + noteControlValue.substring(this.lastSelectedValue.length));
			this.lastSelectedValue = newValue;
		}
	}

	getFirstValidationErrorName(formControlName: string) {
		return Object.keys(this.form.controls[formControlName].errors)[0];
	}

	get controls() {
		return this.form.controls;
	}

	get incidentCodeControl(): FormControl<IIncidentCode | null> {
		return this.form.controls['incidentCode'] as FormControl<IIncidentCode | null>;
	}

	onCancel = () => this.dialogRef.close();

	onSave(): void {
		if (!this.form.valid) return;

		if (this.loading) return;

		this.loading = true;

		const { sequenceNumber, incidentCode, note, employee } = this.form.getRawValue() as {
			sequenceNumber: number;
			incidentCode: IIncidentCode;
			note: string | null;
			employee: string;
		};

		const item = this.chosenItemOperation.items.find((item) => item.sequenceNumber === sequenceNumber);

		const productionNoteRequest: IProductionNotePostRequest = {
			createdBy: employee,
			itemAlias: this.chosenItemOperation.alias,
			note: note === '' ? `(${incidentCode.alias}) ${incidentCode.name}` : note.replace(/[\r\n]+$/, ''),
			sequenceNumber,
			batchId: this.chosenItemOperation.batchId,
		};

		this.productionDataService
			.reorderItemsOnItemOperation(this.chosenItemOperation, item, productionNoteRequest, incidentCode.id)
			.pipe(take(1))
			.subscribe({
				next: (_) => {
					this.reordered.emit();
					this.dialogRef.close();
				},
				error: (err) => {
					this.httpError = err.message;
					this.loading = false;
				},
			});
	}
}
